import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faCalendarAlt,
    faSpinner,
    faTag,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import config from '../config';
import NewsletterSubscribe from '../components/NewsletterSubscribe';
import { User } from '../types/User';
import { formatMarketingCount } from '../utils/formatMarketingCount';
import '../css/pages/NewsletterPage.css';

const INTRO_PREVIEW_MAX_CHARS = 200;
const MAX_TOPIC_TAGS = 4;

// Archive cards are a teaser ("see what a typical week looks like"), not the
// full edition — just the first paragraph of the commentary (which by design
// always covers volume + most-active niches, see weeklyDigest.js) plus topic
// tags, not the full per-sponsor breakdown that's actually in the email.
function getIntroPreview(customIntro?: string): string {
    if (!customIntro) return '';
    const firstParagraph = customIntro.split(/\n\s*\n/)[0].trim();
    const hasMore = firstParagraph.length < customIntro.trim().length;
    if (firstParagraph.length <= INTRO_PREVIEW_MAX_CHARS) {
        return hasMore ? `${firstParagraph}…` : firstParagraph;
    }
    const cut = firstParagraph.slice(0, INTRO_PREVIEW_MAX_CHARS);
    return `${cut.slice(0, cut.lastIndexOf(' '))}…`;
}

function getTopicTags(sponsors: Newsletter['sponsors']): string[] {
    const seen = new Set<string>();
    for (const sponsor of sponsors) {
        for (const tag of sponsor.tags || []) {
            seen.add(tag);
        }
    }
    return Array.from(seen).slice(0, MAX_TOPIC_TAGS);
}

interface NewsletterPageProps {
    user: User;
    userAuth: boolean;
}

interface Newsletter {
    _id: string;
    subject: string;
    customIntro?: string;
    sentAt: string;
    sponsors: Array<{
        _id: string;
        sponsorName: string;
        sponsorLink?: string;
        rootDomain?: string;
        tags?: string[];
        sponsorEmail?: string;
        sponsorApplication?: string;
        contactPersonTitle?: string;
        newslettersSponsored?: Array<{
            newsletterName: string;
            estimatedAudience: number;
            contentTags: string[];
            dateSponsored: string | Date;
        }>;
    }>;
}

const WHATS_INSIDE = [
    'New sponsor count & weekly velocity',
    'Which niches are most active',
    'What the sponsored newsletters have in common',
    'A sample of real sponsor records, with contact info',
];

const NewsletterPage = ({ user, userAuth }: NewsletterPageProps) => {
    const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sponsorCount, setSponsorCount] = useState<number>(0);

    // Check if user is already subscribed
    const isSubscribed = userAuth && user?.newsletterOptIn === true;

    useEffect(() => {
        fetchNewsletters();

        // Same public endpoint Home.tsx uses — ties the report back to the
        // live database size instead of a hardcoded number.
        axios.get(`${config.backendUrl}sponsors/db-info`).then((res) => {
            if (res.data?.sponsors) setSponsorCount(res.data.sponsors);
        }).catch(() => { /* stat strip just skips this card if it fails */ });
    }, []);

    const fetchNewsletters = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${config.backendUrl}users/newsletter/list`);

            if (response.data.success) {
                setNewsletters(response.data.newsletters || []);
            } else {
                setError('Failed to load past reports');
            }
        } catch (err: any) {
            console.error('Error fetching newsletters:', err);
            setError('Failed to load past reports. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="web-page newsletter-page">
            <Helmet>
                <title>The Weekly Sponsor Report — Free Newsletter Sponsor Updates | SponsorDB</title>
                <meta name="description" content="A free weekly email: how many new newsletter sponsors we verified, which niches are most active, and what the sponsored newsletters have in common — plus a sample of real sponsor records." />
                <link rel="canonical" href="https://sponsor-db.com/newsletter/" />
            </Helmet>
            {/* HERO — single centered column, matching the rest of the
                site's section pattern (eyebrow / title / subtitle), with
                the subscribe form as the immediate primary CTA. */}
            <div className="web-section web-section-dark mt-0 newsletter-hero-section">
                <div className="web-section__container web-section-content">
                    <div className="newsletter-hero">
                        <p className="newsletter-hero__eyebrow">Free weekly update · no account required</p>
                        <h1 className="newsletter-hero__title">The Weekly Sponsor Report</h1>
                        <p className="newsletter-hero__description">
                            A 3-minute read, every week: how many new sponsors we verified, which niches are seeing the most sponsorship activity, and what the newsletters landing those deals actually have in common — plus a sample of the sponsors themselves, with contact info.
                        </p>

                        <div className="newsletter-hero__subscribe">
                            <NewsletterSubscribe isSubscribed={isSubscribed} />
                        </div>
                    </div>
                </div>
            </div>

            {/* STAT STRIP — reuses the homepage's stat-card visual pattern
                so this reads as one product, not a bolted-on page. */}
            <div className="web-section newsletter-stats-section mt-0">
                <div className="web-section__container web-section-content">
                    <div className="what-you-get-stats what-you-get-stats--three newsletter-stats">
                        <div className="what-you-get-stat-card">
                            <div className="what-you-get-stat-number">Weekly</div>
                            <div className="what-you-get-stat-label">Delivered every week, free</div>
                        </div>
                        <div className="what-you-get-stat-card">
                            <div className="what-you-get-stat-number">3 min</div>
                            <div className="what-you-get-stat-label">Average read time</div>
                        </div>
                        {sponsorCount > 0 && (
                            <div className="what-you-get-stat-card">
                                <div className="what-you-get-stat-number">{formatMarketingCount(sponsorCount)}</div>
                                <div className="what-you-get-stat-label">Verified sponsors behind the data</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* WHAT'S INSIDE — reuses the homepage's checkmark-tag pattern. */}
            <div className="web-section newsletter-whats-inside-section mt-0">
                <div className="web-section__container web-section-content">
                    <div className="what-you-get-container newsletter-whats-inside">
                        <p className="what-you-get-eyebrow">Every edition includes</p>
                        <div className="what-you-get-tags newsletter-whats-inside-tags">
                            {WHATS_INSIDE.map((item) => (
                                <div className="what-you-get-tag" key={item}>
                                    <FontAwesomeIcon icon={faCheckCircle} className="what-you-get-tag-icon" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ARCHIVE — proof before you subscribe. */}
            <div className="web-section mt-0 newsletter-archive-section">
                <div className="web-section__container web-section-content">
                    <div className="newsletter-content">
                        <div className="newsletter-content__header">
                            <p className="newsletter-content__eyebrow">Archive</p>
                            <h2 className="newsletter-content__title">Past reports</h2>
                            <p className="newsletter-content__subtitle">
                                See what a typical week looks like before you subscribe.
                            </p>
                        </div>

                        {loading && (
                            <div className="newsletter-loading">
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Loading past reports…</span>
                            </div>
                        )}

                        {error && (
                            <div className="newsletter-error">
                                <p>{error}</p>
                            </div>
                        )}

                        {!loading && !error && newsletters.length === 0 && (
                            <div className="newsletter-empty">
                                <FontAwesomeIcon icon={faEnvelope} />
                                <p>The first report hasn't gone out yet — subscribe above and you'll get it.</p>
                            </div>
                        )}

                        {!loading && !error && newsletters.length > 0 && (
                            <div className="newsletter-grid">
                                {newsletters.map((newsletter) => {
                                    const intro = getIntroPreview(newsletter.customIntro);
                                    const topicTags = getTopicTags(newsletter.sponsors);
                                    return (
                                        <div key={newsletter._id} className="newsletter-card">
                                            <div className="newsletter-card__header">
                                                <h3 className="newsletter-card__title">{newsletter.subject}</h3>
                                                <div className="newsletter-card__meta">
                                                    <div className="newsletter-card__meta-item">
                                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                                        <span>{formatDate(newsletter.sentAt)}</span>
                                                    </div>
                                                    <div className="newsletter-card__meta-item">
                                                        <FontAwesomeIcon icon={faEnvelope} />
                                                        <span>{newsletter.sponsors.length} sponsors</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {intro && (
                                                <p className="newsletter-card__intro">{intro}</p>
                                            )}

                                            {topicTags.length > 0 && (
                                                <div className="newsletter-card__topic-tags">
                                                    {topicTags.map((tag) => (
                                                        <span key={tag} className="newsletter-card__topic-tag">
                                                            <FontAwesomeIcon icon={faTag} /> {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsletterPage;
